import { FC, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Swiper } from 'swiper/react';

import { setPublicPageTotalIndexSlides } from 'store/actions/publicPages';
import { RootStateType } from 'store/reducers';
import { EffectFade, Pagination } from 'swiper';
import { ITemplateForMultiTemplate } from 'types/MultiTemplate';
import { IPDFFileInfo } from 'types/signNow/store';
import formSwiperAutoScroll from 'utils/PublicPage/swiperAutoScroll';

type IFormsSliderProps = {
  sliderDisabled?: boolean;
  isFormView?: boolean;
  sliders?: Partial<ITemplateForMultiTemplate | IPDFFileInfo>[];
};

const FormsSlider: FC<IFormsSliderProps> = ({
  children,
  sliderDisabled = false,
  isFormView = false,
  sliders = [],
}) => {
  const dispatch = useDispatch();
  const currentDocument = useSelector((state: RootStateType) => state.publicPages.currentDocument);

  const isPublicPage = currentDocument !== null; // can be also collection for manager
  const currentSlideIndex = currentDocument?.currentSlideIndex || 1;
  const totalIndexSlides = currentDocument?.totalIndexSlides || 0;

  useEffect(() => {
    if (Array.isArray(children)) {
      if (isFormView) {
        const slidesArray = children.filter((child) => child !== null);
        if (totalIndexSlides !== slidesArray.length) {
          dispatch(setPublicPageTotalIndexSlides(slidesArray.length));
        }
      }
    }
  }, [children, isFormView]);

  const getSlideName = (slide: Partial<ITemplateForMultiTemplate | IPDFFileInfo>) => {
    if (slide) {
      if ((slide as ITemplateForMultiTemplate).name) return (slide as ITemplateForMultiTemplate).name;
      if ((slide as IPDFFileInfo).fileKey) return (slide as IPDFFileInfo).fileKey;
    }
    return '';
  };

  if (!children) {
    return (
      <>This slider is empty. Please pass some children element</>
    );
  }

  if (sliderDisabled) {
    return (
      <div className="sections-swiper">
        {children}
      </div>
    );
  }

  return (
    <Swiper
      id="formSwiperScrollable"
      className={isFormView ? 'form-view-swiper' : 'sections-swiper'}
      data-scroll-wrapper
      modules={[Pagination, EffectFade]}
      autoHeight={isPublicPage}
      loop={false}
      noSwiping={isPublicPage}
      noSwipingClass="swiper-slide"
      observer
      observeParents
      watchOverflow={false}
      simulateTouch={false}
      effect="fade"
      fadeEffect={{ crossFade: true }}
      slidesPerView={1}
      spaceBetween={50}
      pagination={
        !isFormView
          ? {
            clickable: true,
            ...(sliders ? { el: '.swiper-custom-pagination' } : {}),
            renderBullet: (index: number, className: string) => {
              if (sliders) {
                return `<button class="${className} swiper-custom-pagination-bullet">${index + 1}. ${getSlideName(sliders[index])}</button>`;
              }
              return `<span class="${className}">${(index + 1)}</span>`;
            },
          }
          : false
      }
      onInit={(swiper) => {
        if (isFormView) swiper.slideTo(currentSlideIndex);
      }}
      onAfterInit={(swiper) => {
        const intervalID = setInterval((swiper) => {
          const templateInfo: HTMLElement | null = document.querySelector('[data-first-slide="true"]');
          if (templateInfo) {
            if (swiper.height < templateInfo.offsetHeight) {
              swiper.update();
            }
          }
          clearInterval(intervalID);
        }, 500, swiper);
      }}
      onSlideChange={() => {
        formSwiperAutoScroll();
      }}
    >
      {children}
    </Swiper>
  );
};

export default FormsSlider;